import { useEffect, useMemo, useReducer } from 'react'
import { useMutation } from '@tanstack/react-query'
import { authReducer, initialAuthState } from './state'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { ASignup } from '@/screens/a-signup/ASignup'
import { AConfirm } from '@/screens/a-confirm/AConfirm'
import { AResendconfirm } from '@/screens/a-resendconfirm/AResendconfirm'
import { ASignin } from '@/screens/a-signin/ASignin'
import { AMfa } from '@/screens/a-mfa/AMfa'
import { AMfaenrol } from '@/screens/a-mfaenrol/AMfaenrol'
import { AForgot } from '@/screens/a-forgot/AForgot'
import { AReset } from '@/screens/a-reset/AReset'
import { AResetnew } from '@/screens/a-resetnew/AResetnew'
import { AChangepw } from '@/screens/a-changepw/AChangepw'
import { AInvite } from '@/screens/a-invite/AInvite'
import { AInvitedead } from '@/screens/a-invitedead/AInvitedead'
import { ASession } from '@/screens/a-session/ASession'

/** Reads `#invitation=<token>` from the URL fragment (§3.1i: "an invite link" / `LLD-IDN-163` —
 * never a path or a query). Real route-driven entry (`/verify`, deep links generally) is Pass
 * 3's; this is the one fragment this pass's own screens need at boot. */
function readInvitationFromFragment(): string | undefined {
  const match = /(?:^|[#&])invitation=([^&]+)/.exec(window.location.hash)
  return match?.[1] ? decodeURIComponent(match[1]) : undefined
}

/**
 * SPA.2 / LLD-SPA-041 — the authentication layer, state-selected per `src/layers/auth/state.ts`.
 * Sixteen of the family's seventeen screens are wired here (`a-verify`/`a-verifyexp` are outside
 * this layer entirely, per rule 4 — see `screens/a-verify/AddressVerificationEntry.tsx`).
 *
 * `initialScreen` is `AppRoot`'s own hook for `a-session`: a refresh-failure/session-ended event
 * fires from deep inside `ShellLayer`, well past this component's own mount, so `AppRoot` remounts
 * this layer pinned to `a-session` rather than this layer discovering that state itself.
 */
export function AuthLayer({ onAuthenticated, initialScreen }: { onAuthenticated: () => void; initialScreen?: 'a-session' | 'a-changepw' }) {
  const invitationFromLink = useMemo(() => readInvitationFromFragment(), [])
  const [state, dispatch] = useReducer(
    authReducer,
    initialScreen ? { screen: initialScreen } : invitationFromLink ? { screen: 'a-invite', invitation: invitationFromLink } : initialAuthState,
  )

  useEffect(() => {
    if (state.screen === 'authenticated') onAuthenticated()
  }, [state.screen, onAuthenticated])

  // Whatever path lands here — `a-changepw`'s own cancel/success, `PASSWORD_RESET`,
  // `CHALLENGE_EXPIRED` — `a-signin` means no session is meant to still be live. Cleared
  // uniformly here rather than at every individual transition into it.
  useEffect(() => {
    if (state.screen === 'a-signin') setAccessToken(null)
  }, [state.screen])

  const acceptInvitation = useMutation({
    mutationFn: async (invitation: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/invitations/accept', { body: { invitation } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSettled: () => dispatch({ type: 'INVITE_ACCEPTED_EXISTING_USER' }),
  })

  if (state.screen === 'accepting-invitation') {
    if (!acceptInvitation.isPending && !acceptInvitation.isSuccess && !acceptInvitation.isError) {
      acceptInvitation.mutate(state.invitation)
    }
    return null
  }

  switch (state.screen) {
    case 'a-signup':
      return (
        <ASignup
          onSignedUp={(email, invitation) => dispatch({ type: 'SIGNED_UP', email, invitation })}
          onGoToSignIn={() => dispatch({ type: 'GO_TO_SIGNIN' })}
        />
      )
    case 'a-confirm':
      return <AConfirm email={state.email} invitation={state.invitation} onConfirmed={() => dispatch({ type: 'CONFIRMED' })} />
    case 'a-resendconfirm':
      return (
        <AResendconfirm
          email={state.email}
          onSent={(email) => dispatch({ type: 'RESENT', email })}
          onBackToSignIn={() => dispatch({ type: 'GO_TO_SIGNIN' })}
        />
      )
    case 'a-signin':
      return (
        <ASignin
          banner={state.banner}
          onSignedIn={(email, challenge) => dispatch({ type: 'SIGNED_IN', email, challenge })}
          onGoToForgot={() => dispatch({ type: 'GO_TO_FORGOT' })}
          onGoToSignUp={() => dispatch({ type: 'GO_TO_SIGNUP' })}
          onAccountUnconfirmed={(email) => dispatch({ type: 'ACCOUNT_UNCONFIRMED', email })}
          onPasswordResetRequired={() => dispatch({ type: 'PASSWORD_RESET_REQUIRED' })}
        />
      )
    case 'a-mfa':
      return (
        <AMfa
          continuation={state.continuation}
          email={state.email}
          onAnswered={() => dispatch({ type: 'CHALLENGE_ANSWERED' })}
          onExpired={() => dispatch({ type: 'CHALLENGE_EXPIRED' })}
          onBackToSignIn={() => dispatch({ type: 'GO_TO_SIGNIN' })}
        />
      )
    case 'a-mfaenrol':
      return <AMfaenrol continuation={state.continuation} email={state.email} onEnrolled={() => dispatch({ type: 'ENROLLED' })} />
    case 'a-forgot':
      return <AForgot onRequested={(email) => dispatch({ type: 'RESET_REQUESTED', email })} onBackToSignIn={() => dispatch({ type: 'GO_TO_SIGNIN' })} />
    case 'a-reset':
      return <AReset email={state.email} onHaveCode={() => dispatch({ type: 'GO_TO_RESETNEW' })} />
    case 'a-resetnew':
      return <AResetnew email={state.email} onReset={() => dispatch({ type: 'PASSWORD_RESET' })} />
    case 'a-changepw':
      return <AChangepw onChanged={() => dispatch({ type: 'PASSWORD_CHANGED' })} onCancel={() => dispatch({ type: 'GO_TO_SIGNIN' })} />
    case 'a-invite':
      return (
        <AInvite
          invitation={state.invitation}
          onSignedUpForAccept={(email, invitation) => dispatch({ type: 'SIGNED_UP', email, invitation })}
          onGoToSignInToAccept={(invitation) => dispatch({ type: 'GO_TO_SIGNIN_TO_ACCEPT', invitation })}
          onDead={() => dispatch({ type: 'INVITE_DEAD' })}
        />
      )
    case 'a-invitedead':
      return <AInvitedead onBackToSignIn={() => dispatch({ type: 'GO_TO_SIGNIN' })} />
    case 'a-session':
      return <ASession onSignedOut={() => dispatch({ type: 'GO_TO_SIGNIN' })} />
    case 'authenticated':
      // `onAuthenticated` (above) has already fired. `src/layers/shell/OrgResolver.tsx` takes it
      // from here — this layer renders nothing once it is done.
      return null
  }
}
